/**
 * Журнал платных вызовов ИИ: тип ответа `/api/calls` (форма `aicall.calls`).
 * Деньги и секунды — числа или `null` (вызов ещё идёт или не дошёл до провайдера).
 * Коды вида и статуса — как в `aicall.KINDS` / `aicall.STATUSES`.
 */
import { apiFetch, query } from "@/lib/api";

export type CallKind = "persona_image" | "persona_compose" | "shot_generation" | "trend_translate" | "fitting" | "reference_generation" | "manual_generation" | "video_scenario" | "video_clip" | "enhance" | "search_translate" | "tag_translate" | "post_text" | "video_ref_describe" | "clip_prompt";
export type CallStatus = "running" | "ok" | "provider_error" | "validation_failed" | "timeout" | "lost";

/** К чему привязан вызов; пустые поля сервис выбрасывает. */
export type CallLink = {
  persona_id?: number;
  image_id?: number;
  prompt_id?: number;
  project_id?: number;
  shot_id?: number;
  generation_id?: number;
  pin_id?: number;
  reference_item_id?: number;
  /** генерация по удалённому референсу: ссылки нет, «был: …» — из снимка генерации */
  reference_deleted?: { id: number | null; library_name: string | null };
};

export type CallRow = {
  id: number;
  kind: CallKind;
  status: CallStatus;
  provider: string;
  model: string;
  author_user_id: number;
  author_name: string;
  attempt: number;
  params: Record<string, unknown>;
  http_status: number | null;
  error: string | null;
  cost: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  seconds: number | null;
  date_create: string;
  date_finish: string | null;
  assets: string[];
  link: CallLink;
};

export type CallsResult = {
  calls: CallRow[];
  total: number;
  totals: {
    count: number;
    cost: number;
    by_user: { user_id: number; name: string; count: number; cost: number }[];
    by_model: { model: string; count: number; cost: number }[];
  };
  users: { user_id: number; name: string | null }[];
  models: string[];
};

export type CallsQuery = {
  user_id?: string;
  model?: string;
  kind?: string;
  from?: string;
  to?: string;
  offset?: number;
};

export const callsKeys = { list: (q: CallsQuery) => ["calls", q] as const };

export const loadCalls = (q: CallsQuery) => apiFetch<CallsResult>("/api/calls" + query({ ...q, limit: 200 }));

export const KIND_LABEL: Record<string, string> = {
  persona_image: "Генерация героини",
  persona_compose: "Составление промпта",
  shot_generation: "Генерация кадра",
  trend_translate: "Перевод запросов трендов",
  fitting: "Примерка",
  reference_generation: "Генерация по референсу",
  manual_generation: "Генерация вручную",
  video_scenario: "Сценарий ролика",
  video_clip: "Видео-клип",
  enhance: "Улучшение",
  search_translate: "Перевод поиска",
  tag_translate: "Перевод промпта тега",
  post_text: "Текст публикации",
  video_ref_describe: "Описание видео-референса",
  clip_prompt: "Промпт клипа",
};

export const STATUS_LABEL: Record<string, string> = {
  running: "Выполняется",
  ok: "Готово",
  provider_error: "Отказ провайдера",
  validation_failed: "Не прошёл проверку",
  timeout: "Таймаут",
  lost: "Потерян: воркер перезапущен",
};

/** Куда вести из строки журнала: картинка/промпт героини, кадр проекта или никуда. */
export function callLink(c: CallRow): string | null {
  const l = c.link;
  if (l.reference_item_id !== undefined) return `/lib/ref/${l.reference_item_id}`;
  if (l.pin_id !== undefined) return `/pins/${l.pin_id}`;
  if (l.persona_id !== undefined) {
    if (l.image_id !== undefined) return `/persona/${l.persona_id}?image=${l.image_id}`;
    if (l.prompt_id !== undefined) return `/persona/${l.persona_id}?prompt=${l.prompt_id}`;
    return `/persona/${l.persona_id}`;
  }
  if (l.project_id !== undefined && l.shot_id !== undefined) return `/project/${l.project_id}/shot/${l.shot_id}`;
  // ручная генерация песочницы: владельца нет, есть только сама генерация
  if (c.kind === "manual_generation" && l.generation_id !== undefined) return `/generate?from=${l.generation_id}`;
  // текст публикации: привязка — только plan_id в параметрах вызова
  if (c.kind === "post_text" && c.params.plan_id !== undefined) return `/posts/${String(c.params.plan_id)}`;
  // описание видео-референса: привязка — video_ref_id в параметрах вызова
  if (c.kind === "video_ref_describe" && c.params.video_ref_id) return `/video-refs/${String(c.params.video_ref_id)}`;
  return null;
}
